import React, { useState, useEffect, useCallback } from 'react';
import { useMatchingGrid } from './MatchingGridContext';
import { CardData, TapData, RoundData } from './types';
import { adjustDifficulty } from './dda';
import { generateCardImages } from './cardImages';
import { exportToCSV } from './export';
import { useAuth } from '../../context/FirebaseAuthContext';
import { useGameSession } from '../../context/GameSessionContext';
import { GameResultSaver } from '../../utils/gameResultSaver';
import './MatchingGrid.css';

const MatchingGrid: React.FC = () => {
  const {
    currentSettings,
    updateSettings,
    saveRoundData,
    ddaState,
    updateDDAState
  } = useMatchingGrid();
  
  const { user } = useAuth();
  const { currentSession } = useGameSession();

  const [cards, setCards] = useState<CardData[]>([]);
  const [flippedCards, setFlippedCards] = useState<CardData[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [gameStartTime, setGameStartTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [roundNumber, setRoundNumber] = useState(1);
  const [taps, setTaps] = useState<TapData[]>([]);
  const [showPreview, setShowPreview] = useState(true);
  const [roundMetrics, setRoundMetrics] = useState({
    correctPairs: 0,
    totalAttempts: 0,
    errors: 0,
  });

  // Update current time every second for live timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const initializeCards = useCallback(() => {
    const { rows, cols } = currentSettings.gridSize;
    const cardImages = generateCardImages(roundNumber, { rows, cols });
    
    const newCards: CardData[] = cardImages.map(img => ({
      id: img.id,
      imageId: img.similarityGroup || 0,
      content: img.content,
      isFlipped: true,
      isMatched: false
    }));

    // Shuffle cards
    const shuffled = [...newCards].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setFlippedCards([]);
    setTaps([]);
    setRoundMetrics({
      correctPairs: 0,
      totalAttempts: 0,
      errors: 0,
    });
    
    // Show preview
    setShowPreview(true);
    setTimeout(() => {
      setCards(cards => cards.map(card => ({ ...card, isFlipped: false })));
      setShowPreview(false);
      setGameStartTime(Date.now());
    }, currentSettings.previewTime);
  }, [currentSettings.gridSize, currentSettings.previewTime]);

  useEffect(() => {
    initializeCards();
  }, [initializeCards]);

  const handleCardClick = useCallback((card: CardData) => {
    if (isLocked || card.isMatched || card.isFlipped || showPreview) return;

    const tapTime = Date.now();
    const newFlippedCards = [...flippedCards, card];
    
    setCards(prev => 
      prev.map(c => c.id === card.id ? { ...c, isFlipped: true } : c)
    );
    setFlippedCards(newFlippedCards);

    if (newFlippedCards.length === 2) {
      setIsLocked(true);
      const isMatch = newFlippedCards[0].imageId === newFlippedCards[1].imageId;

      setTaps(prev => [
        ...prev,
        {
          cardId: card.id,
          imageId: card.imageId,
          tapTime: tapTime - gameStartTime,
          isCorrect: isMatch
        }
      ]);

      setRoundMetrics(prev => ({
        ...prev,
        totalAttempts: prev.totalAttempts + 1,
        correctPairs: isMatch ? prev.correctPairs + 1 : prev.correctPairs,
        errors: isMatch ? prev.errors : prev.errors + 1
      }));

      setTimeout(() => {
        setCards(prev =>
          prev.map(c => {
            if (newFlippedCards.some(fc => fc.id === c.id)) {
              return {
                ...c,
                isFlipped: isMatch,
                isMatched: isMatch
              };
            }
            return c;
          })
        );
        setFlippedCards([]);
        setIsLocked(false);

        // Check if round is complete
        if (isMatch && roundMetrics.correctPairs + 1 === currentSettings.gridSize.rows * currentSettings.gridSize.cols / 2) {
          endRound();
        }
      }, currentSettings.flipBackDelay);
    }
  }, [isLocked, flippedCards, showPreview, currentSettings.flipBackDelay, gameStartTime, roundMetrics.correctPairs, currentSettings.gridSize]);

  const endRound = useCallback(() => {
    const endTime = Date.now();
    const totalTime = endTime - gameStartTime;

    const roundData: RoundData = {
      roundNumber,
      gridSize: currentSettings.gridSize,
      flipBackDelay: currentSettings.flipBackDelay,
      previewTime: currentSettings.previewTime,
      hintsEnabled: currentSettings.hintsEnabled,
      imageSimilarityLevel: currentSettings.imageSimilarityLevel,
      correctPairs: roundMetrics.correctPairs,
      totalAttempts: roundMetrics.totalAttempts,
      errors: roundMetrics.errors,
      totalTime,
      averageReactionTime: totalTime / (roundMetrics.totalAttempts * 2),
      accuracy: roundMetrics.correctPairs / roundMetrics.totalAttempts,
      timestamp: new Date().toISOString(),
      taps
    };

    saveRoundData(roundData);

    // Save to database
    const saveToDatabase = async () => {
      const accuracy = (roundMetrics.correctPairs / roundMetrics.totalAttempts) * 100;
      const pairsCount = (currentSettings.gridSize.rows * currentSettings.gridSize.cols) / 2;
      const score = Math.round(accuracy * 10 + (pairsCount * 50) - (roundMetrics.errors * 10));

      const resultData = {
        sessionId: currentSession?.sessionId || `session-${Date.now()}`,
        timestamp: new Date().toISOString(),
        round: roundNumber,
        gridSize: currentSettings.gridSize.rows * currentSettings.gridSize.cols,
        pairsCount: Math.round(pairsCount),
        movesCount: roundMetrics.totalAttempts,
        matchesFound: roundMetrics.correctPairs,
        timeSeconds: Math.round(totalTime / 1000),
        accuracyPercentage: accuracy,
        score: score,
        difficultyLevel: Math.round(currentSettings.imageSimilarityLevel * 10), // Convert to integer
        isCompleted: roundMetrics.correctPairs === pairsCount,
        cardLayout: JSON.stringify(cards.map(c => ({ id: c.id, imageId: c.imageId }))),
        playerMoves: JSON.stringify(taps),
        timingData: JSON.stringify({
          totalTime,
          averageReactionTime: totalTime / (roundMetrics.totalAttempts * 2),
          flipBackDelay: currentSettings.flipBackDelay,
          previewTime: currentSettings.previewTime
        })
      };

      await GameResultSaver.saveResultSafely(
        () => GameResultSaver.saveImageMatchingResult(resultData),
        'ImageMatching',
        resultData
      );
    };

    saveToDatabase();

    // Apply DDA
    const { newSettings, newDDAState } = adjustDifficulty(
      currentSettings,
      {
        ...roundMetrics,
        totalTime,
        averageReactionTime: totalTime / (roundMetrics.totalAttempts * 2),
        accuracy: roundMetrics.correctPairs / roundMetrics.totalAttempts
      },
      ddaState
    );

    updateSettings(newSettings);
    updateDDAState(newDDAState);
    setRoundNumber(prev => prev + 1);
    
    // Start next round
    setTimeout(initializeCards, 1500);
  }, [
    gameStartTime,
    roundNumber,
    currentSettings,
    roundMetrics,
    taps,
    cards,
    saveRoundData,
    ddaState,
    updateSettings,
    updateDDAState,
    initializeCards,
    currentSession
  ]);

  const { roundHistory } = useMatchingGrid();

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentRoundTime = () => {
    if (gameStartTime === 0) return 0;
    return Math.floor((currentTime - gameStartTime) / 1000);
  };

  const handleExport = (format: 'csv' | 'json') => {
    if (roundHistory.length === 0) {
      alert('No data to export yet. Play some rounds first!');
      return;
    }

    const timestamp = new Date().toISOString().split('T')[0];
    const data = format === 'csv' ? exportToCSV(roundHistory) : JSON.stringify(roundHistory, null, 2);
    const blob = new Blob([data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `matching-grid-results-${timestamp}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="matching-grid-container">
      <div className="status-bar">
        <div>Round: {roundNumber}</div>
        <div>Time: {formatTime(getCurrentRoundTime())}</div>
        <div>Accuracy: {((roundMetrics.correctPairs / Math.max(1, roundMetrics.totalAttempts)) * 100).toFixed(1)}%</div>
        <div>Grid: {currentSettings.gridSize.rows}×{currentSettings.gridSize.cols}</div>
      </div>

      <div 
        className="grid"
        style={{
          gridTemplateColumns: `repeat(${currentSettings.gridSize.cols}, 1fr)`,
          gridTemplateRows: `repeat(${currentSettings.gridSize.rows}, 1fr)`
        }}
      >
        {cards.map(card => (
          <div
            key={card.id}
            className={`card ${card.isFlipped ? 'flipped' : ''} ${card.isMatched ? 'matched' : ''}`}
            onClick={() => handleCardClick(card)}
          >
            <div className="card-inner">
              <div className="card-front"></div>
              <div className="card-back">
                <span className="card-content">{card.content}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="controls">
        <button onClick={initializeCards}>New Game</button>
        <button onClick={() => handleExport('csv')}>Export CSV</button>
        <button onClick={() => handleExport('json')}>Export JSON</button>
      </div>
    </div>
  );
};

export default MatchingGrid;